import { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxArchive, faCity, faDownload, faShippingFast, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./styles.module.scss";

import StatusManager from "../../../../../FunctionalComponents/StatusManager/StatusManager";
import Image from "next/image";
import Link from "next/link";

import { getStepsAndCurrentStep } from './Utilities/orderSteps';

import successMsg from '../../../../../Functions/successMsg';
import errorMsg from '../../../../../Functions/errorMsg';

import ProductContainer from "./Components/ProductContainer/ProductContainer";
import ServiceContainer from "./Components/ServiceContainer/ServiceContainer";

import QRCodeScanner from '../../../../../GlobalComponents/QRCodeScanner/QRCodeScanner';

import OrderPDF from "./Utilities/OrderPDF";

import PuffLoader from "react-spinners/PuffLoader";

import { PDFDownloadLink } from "@react-pdf/renderer";

export default function ViewOrder({ orderID, close, addOrderToUpdate }) {

  const [hasError, setHasError] = useState(false);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ visible: false, stepIndex: null, stepLabel: null });

  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("");

  useEffect(() => {
    if (order || !orderID || hasError) return;

    async function fetchOrder() {
      try {
        const response = await axios.post(`${window.location.origin}/api/vendor/manage/orders/getOrderData`, { orderID });

        if(response && response.status === 200) {
          const orderData = response.data;
          setOrder(orderData);

          if(orderData?.shipping?.trackingNumber && orderData?.shipping?.carrier) {
            setTrackingNumber(orderData.shipping.trackingNumber);
            setCarrier(orderData.shipping.carrier);
          }

        }
        
      } catch (error) {
        console.error("Error fetching order:", error);
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true);
      } finally {
        setLoading(false);
      }
    }

    fetchOrder();
  }, [orderID, order, hasError]);

  /* QR Handling Logic */

  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScannerKey, setQrScannerKey] = useState(0);
  
  if (loading)
    return (
      <div className={styles.overlay} onClick={close}>
        <div className={styles.modal}>
          <p>Loading order details...</p>
        </div>
      </div>
    );

    if (refreshing)
      return (
        <div className={styles.overlay} onClick={close}>
          <div className={styles.modal}>
            <p>We're updating this order, please wait...</p>
          </div>
        </div>
      );

  if (!order)
    return (
      <div className={styles.overlay} onClick={close}>
        <div className={styles.modal}>
          <p>Order not found</p>
        </div>
      </div>
    );

    const { orderType, customer, payment, items, shipping } = order;
    const orderStatus = order.status;
    const hasShippingInfo = shipping?.trackingNumber && shipping.carrier
    const isCancelled = orderStatus === "Cancelled";
    const hasShipping = !!customer.shippingAddress;

    const { steps, currentStep } = getStepsAndCurrentStep(order.orderType, orderStatus, hasShipping);

    const handleStepClicked = async (stepIndex, stepLabel) => {
      setConfirmAction({ visible: true, stepIndex, stepLabel });
    };
    
    const confirmStepChange = async () => {
      if (!orderID || !confirmAction.stepLabel) return;
      setRefreshing(true);
      setConfirmAction({ visible: false, stepIndex: null, stepLabel: null });
    
      try {
        const response = await axios.post(
          `${window.location.origin}/api/vendor/manage/orders/updateOrderStatus`,
          {
            orderID,
            newStatus: confirmAction.stepLabel,
          }
        );
    
        if (response && response.status === 200) {
          successMsg(response.data.message);
          setOrder(response.data.updatedOrder);
    
          const orderToUpdateDataset = {
            orderID: response.data.updatedOrder.orderID,
            status: response.data.updatedOrder.status,
          };
    
          addOrderToUpdate(orderToUpdateDataset);
          setShowQRScanner(null);
        }
      } catch (error) {
        console.error("Error updating order status:", error);
        errorMsg(error?.response?.data?.status || error.toString());
      } finally {
        setRefreshing(false);
      }
    };
    

    const cancelConfirmation = () => {
      setConfirmAction({ visible: false, stepIndex: null, stepLabel: null });
    };

    const handleCancelOrder = () => {
      setConfirmAction({ visible: true, stepIndex: -1, stepLabel: 'Cancelled' });
    };

    const handleOrderQRScanned = async (code) => {
      if (refreshing) return;
      setRefreshing(true);
      try {
        const response = await axios.post(`${window.location.origin}/api/vendor/manage/orders/updatePaymentStatus`, {
          orderID,
          paymentIntentID: order.payment.paymentIntentID,
          qrCode: code,
        });

        if (response.status === 200) {
          successMsg(response.data.message);
          setOrder(response.data.updatedOrder);

          const orderToUpdateDataset = {
            orderID: response.data.updatedOrder.orderID,
            status: response.data.updatedOrder.status,
          };

          addOrderToUpdate(orderToUpdateDataset);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
      } finally {
        setRefreshing(false);
        setShowQRScanner(null);
        setQrScannerKey((prevKey) => prevKey + 1);
      }
    };
  
    const handleOrderShipped = () => {
      setShowTrackingModal(true);
    };
  
    const submitTrackingDetails = async () => {
      if (!trackingNumber || !carrier) {
        errorMsg("Please enter both the tracking number and carrier.");
        return;
      }
  
      setRefreshing(true);
  
      try {
        const response = await axios.post(
          `${window.location.origin}/api/vendor/manage/orders/updateOrderStatus`,
          {
            orderID,
            newStatus: "Shipped",
            trackingNumber,
            carrier,
          }
        );
  
        if (response && response.status === 200) {
          successMsg(response.data.message);
          setOrder(response.data.updatedOrder);
  
          addOrderToUpdate({
            orderID: response.data.updatedOrder.orderID,
            status: response.data.updatedOrder.status,
          });
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
      } finally {
        setRefreshing(false);
        setShowTrackingModal(false);
      }
    };
    

  return (
    <div className={styles.overlay} onClick={close} >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* QR Code Scanner */}
        {showQRScanner && <QRCodeScanner key={qrScannerKey} closeScanner={() => setShowQRScanner(false)} onScan={handleOrderQRScanned} scanDelay={5000} /> }

        {/* Confirmation Modal */}
        {confirmAction.visible && (
          <div className={styles.confirmContainer}>
            <div>
              <h2>Confirm Action</h2>
              <p>
                Are you sure you want to move to step:{" "}
                <b>{confirmAction.stepLabel}</b>?
              </p>
              <div className={styles.confirmButtons}>
                <button onClick={confirmStepChange} className={styles.confirm}>
                  Confirm
                </button>
                <button onClick={cancelConfirmation} className={styles.cancel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {showTrackingModal && (
          <div className={styles.trackingContainer}>
            <div>
              <h2>Enter Tracking <FontAwesomeIcon icon={faShippingFast} id={styles.icon} /></h2>
              <section>
                <h1>Tracking #</h1>
                <input type='text' value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} />
              </section>
              <section>
                <h1>Carrier</h1>
                <input type='text' value={carrier} onChange={(e) => setCarrier(e.target.value)} />
              </section>
              <div className={styles.confirmButtons}>
                <button onClick={submitTrackingDetails} className={styles.confirm}>
                  Confirm
                </button>
                <button onClick={() => setShowTrackingModal(false)} className={styles.cancel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.orderHeader} >
          <nav>
            <FontAwesomeIcon icon={faXmark} className={styles.closeButton} onClick={close} />
            <PDFDownloadLink document={<OrderPDF order={order} />} fileName={`Order-${order.orderID}.pdf`} >
                {({ loading }) =>
                  loading ? (
                    <button className={styles.downloadButton}>
                      <PuffLoader size={30} color="#818181" />
                    </button>
                    
                  ) : (
                    <button className={styles.downloadButton} style={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faDownload} id={styles.icon} />
                    </button>
                  )
                }
              </PDFDownloadLink>
          </nav>
          <div className={styles.clientSection}>
            <section>
              <div>
                <h1>{order.customer.name}</h1>
                <Link href={`tel:${order.customer.phone}`} target='_blank'>
                  {order.customer.phone}
                </Link>
                <Link href={`mailto:${order.customer.email}`}>
                  {order.customer.email}
                </Link>
                {order.orderType === 'product' && (
                  hasShipping ? (
                    <section id={styles.shippingInfo}>
                      <h2>Shipping Address</h2>
                      <div>
                        <p>{customer.shippingAddress.address}</p>
                        <p>
                          {customer.shippingAddress.city} {customer.shippingAddress.state},{" "}
                          {customer.shippingAddress.zip}
                        </p>
                        <p>{customer.shippingAddress.country}</p>
                      </div>
                      { hasShippingInfo && <section>
                        <h1>Tracking Information</h1>
                        <div>
                          <h1>Tracking #:</h1>
                          <h2>{shipping.trackingNumber}</h2>
                        </div>
                        <div>
                          <h1>Carrier:</h1>
                          <h2>{shipping.carrier}</h2>
                        </div>
                        { orderStatus === 'Shipped' && <button onClick={handleOrderShipped}>Edit Tracking</button> }
                      </section>
                      }
                    </section>
                  ) : (
                    <h2 style={{ marginTop: "15px" }}>Local Pickup <FontAwesomeIcon icon={faCity} id={styles.icon} /></h2>
                  )
                )}
              </div>
              <div>
                <div>
                  <Image sizes='auto auto' fill objectFit='cover' src='/waco_exchange_logo_green.png' alt="Alt txt" />
                </div>
                <Link href={`/users/${order.customer.username}`} target="_blank">View Profile</Link>
              </div>
            </section>
            <section>

            </section>
          </div>
          <hr />
          <section>
            <div>
              <h1>Order ID:</h1>
              <h2>{order.orderID}</h2>
            </div>
            <div>
              <h1>Order Type:</h1>
              <h2>{order.orderType.charAt(0).toUpperCase() + order.orderType.slice(1)}</h2>
            </div>
            <div>
              <h1>Created:</h1>
              <h2>{new Date(order.createdAt).toLocaleDateString()}</h2>
            </div>
            <div>
              <h1>Last Updated:</h1>
              <h2>{new Date(order.updatedAt).toLocaleDateString()}</h2>
            </div>
          </section>

        </div>

        { order.isArchived ? <div className={styles.orderArchivedContainer}> <FontAwesomeIcon icon={faBoxArchive} id={styles.icon} /> <h1>Order Archived</h1> </div> : <div className={styles.orderStatusContainer}>
          <StatusManager
            steps={steps}
            initialStep={currentStep}
            isCancelled={isCancelled}
            stepClicked={(index, label) => {
              if (label === "Order Shipped") {
                handleOrderShipped();
              } else {
                handleStepClicked(index, label);
              }
            }}
            handleCancelOrder={handleCancelOrder}
            onCompleteStep={() => setShowQRScanner(true)}
          />
        </div> }

        <div className={styles.orderDetails}>
          {orderType === "product" && <ProductContainer items={items} />}
          {orderType === "service" && <ServiceContainer service={order} />}
        </div>

      </div>
    </div>
  );
}